import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { restoreCart } from '../utils/cartPersistence';

// Cart Item Interface
export interface CartItem {
  marketId: string;
  marketName: string;
  priceId: string;
  price: number;
  productId: string;
  interval: 'monthly' | 'yearly'; // NEW: Add interval field
}

// Cart Context Type
interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (marketId: string) => void;
  clearCart: () => void;
  totalPrice: number;
  itemCount: number;
  isItemInCart: (marketId: string) => boolean;
  canAddToCart: () => boolean;
  maxItems: number;
  getCartInterval: () => 'monthly' | 'yearly' | null;
  onIntervalMismatch?: (cartInterval: string, newInterval: string) => void;
}

// Create Context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Local Storage Key
const CART_STORAGE_KEY = 'horwath-cart';

// Cart Limits
const MAX_CART_ITEMS = 10;

// Migration function for existing cart data
const migrateCartItems = (items: any[]): CartItem[] => {
  return items.map(item => ({
    ...item,
    interval: item.interval || 
      (item.productId.includes('monthly') ? 'monthly' : 'yearly')
  }));
};

// Function to initialize cart from localStorage
const initializeCart = (): CartItem[] => {
  console.log('🛒 CartProvider: Initializing cart from localStorage...');
  console.log('🛒 CartProvider: Current URL:', window.location.href);
  console.log('🛒 CartProvider: URL hash:', window.location.hash);
  
  try {
    // First attempt to restore cart from backups (if needed)
    const restored = restoreCart();
    console.log('🛒 CartProvider: Cart restore attempted, result:', restored);
    
    // Now load from localStorage
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    console.log('🛒 CartProvider: Cart data from localStorage:', savedCart);
    console.log('🛒 CartProvider: SessionStorage backup:', sessionStorage.getItem('horwath-cart-backup'));
    console.log('🛒 CartProvider: OAuth backup:', sessionStorage.getItem('horwath-cart-oauth-backup'));
    
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      if (Array.isArray(parsedCart)) {
        // Migrate cart items to include interval if missing
        const migratedCart = migrateCartItems(parsedCart);
        console.log('🛒 CartProvider: Cart initialized with', migratedCart.length, 'items');
        console.log('🛒 CartProvider: Cart items:', migratedCart);
        return migratedCart;
      }
    }
    console.log('🛒 CartProvider: No cart data found, starting with empty cart');
    return [];
  } catch (error) {
    console.error('🛒 CartProvider: Error loading cart from localStorage:', error);
    return [];
  }
};

// Cart Provider Component
export const CartProvider: React.FC<{ children: ReactNode; onIntervalMismatch?: (cartInterval: string, newInterval: string) => void }> = ({ children, onIntervalMismatch }) => {
  // Initialize state with cart from localStorage
  const [items, setItems] = useState<CartItem[]>(initializeCart);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  // Add item to cart
  const addItem = (item: CartItem) => {
    setItems(prevItems => {
      // Check if item already exists (prevent duplicates)
      const existingItem = prevItems.find(i => i.marketId === item.marketId);
      if (existingItem) {
        console.warn(`Market ${item.marketName} is already in cart`);
        return prevItems;
      }
      
      // NEW: Check interval compatibility
      if (prevItems.length > 0) {
        const cartInterval = prevItems[0].interval;
        if (item.interval !== cartInterval) {
          // Trigger interval mismatch handling
          if (onIntervalMismatch) {
            onIntervalMismatch(cartInterval, item.interval);
          }
          return prevItems; // Don't add the item
        }
      }
      
      // Check cart limit
      if (prevItems.length >= MAX_CART_ITEMS) {
        console.warn(`Cart limit reached. Maximum ${MAX_CART_ITEMS} items allowed.`);
        // You might want to show a notification to the user here
        return prevItems;
      }
      
      // Validate price is positive
      if (item.price <= 0) {
        console.error('Invalid price: Price must be greater than 0');
        return prevItems;
      }
      
      // Add new item
      return [...prevItems, item];
    });
  };

  // Remove item from cart
  const removeItem = (marketId: string) => {
    setItems(prevItems => prevItems.filter(item => item.marketId !== marketId));
  };

  // Clear entire cart
  const clearCart = () => {
    setItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  // Calculate total price
  const totalPrice = items.reduce((sum, item) => sum + item.price, 0);

  // Get item count
  const itemCount = items.length;

  // Check if item is in cart
  const isItemInCart = (marketId: string): boolean => {
    return items.some(item => item.marketId === marketId);
  };

  // Check if we can add more items to cart
  const canAddToCart = (): boolean => {
    return items.length < MAX_CART_ITEMS;
  };

  // Get the current cart interval
  const getCartInterval = (): 'monthly' | 'yearly' | null => {
    if (items.length === 0) return null;
    return items[0].interval;
  };

  const value: CartContextType = {
    items,
    addItem,
    removeItem,
    clearCart,
    totalPrice,
    itemCount,
    isItemInCart,
    canAddToCart,
    maxItems: MAX_CART_ITEMS,
    getCartInterval,
    onIntervalMismatch,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper function to format price
export const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100); // Convert from cents to dollars
};